import type { JSX, KeyboardEvent, ReactNode } from "react";
import { useState } from "react";
import { GripVertical, Plus, X } from "lucide-react";
import type { AnalyticsFieldKey } from "@adlywise/shared";
import type { AnalyticsCategory } from "@/features/analytics/filterCategories";

// Building blocks of the side panel's row lists (Group by, Sort by, Filters,
// Columns): a reorderable list, the Category → Field picker a new row starts
// as, and the empty "add" line under a list.

// ── category helpers ────────────────────────────────────────────────────────

// The categories narrowed to `keys`, in their own order; empty ones dropped, so
// an exhausted category is never offered.
export const narrowCategories = (
  categories: AnalyticsCategory[],
  keep: (key: AnalyticsFieldKey) => boolean,
): AnalyticsCategory[] =>
  categories
    .map((category) => ({
      ...category,
      fields: category.fields.filter(keep),
    }))
    .filter((category) => category.fields.length > 0);

export const fieldLabelIn = (
  categories: AnalyticsCategory[],
  key: AnalyticsFieldKey,
): { category: string; field: string } | null => {
  for (const category of categories) {
    if (category.fields.includes(key)) {
      return {
        category: category.label,
        field: category.fieldDescriptors[key]?.label ?? key,
      };
    }
  }
  return null;
};

// "Location › City" as a node; the category part muted.
export const FieldName = ({
  categories,
  fieldKey,
  fallback,
}: {
  categories: AnalyticsCategory[];
  fieldKey: AnalyticsFieldKey;
  fallback: string;
}): JSX.Element => {
  const label = fieldLabelIn(categories, fieldKey);
  if (!label) return <>{fallback}</>;
  return (
    <>
      <span className="text-(--text-muted)">{label.category} › </span>
      {label.field}
    </>
  );
};

// ── reorderable list ────────────────────────────────────────────────────────

export type OrderedRow = {
  key: string;
  label: ReactNode;
  // Plain-text name for the move / remove buttons.
  name: string;
  removable?: boolean;
};

// Rows are dragged with the mouse (native drag and drop, within this list only)
// or moved with ArrowUp / ArrowDown on the grip. `startNumber` numbers the rows
// when the list continues another one (Columns: groupings follow Time).
export const OrderedRows = ({
  rows,
  onMove,
  onRemove,
  startNumber = 1,
  ariaLabel,
}: {
  rows: OrderedRow[];
  onMove?: (from: number, to: number) => void;
  onRemove?: (key: string) => void;
  startNumber?: number;
  ariaLabel?: string;
}): JSX.Element | null => {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  if (rows.length === 0) return null;

  const canMove = onMove !== undefined && rows.length > 1;
  const endDrag = (): void => {
    setDragFrom(null);
    setDragOver(null);
  };

  const handleGripKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    const to =
      event.key === "ArrowUp"
        ? index - 1
        : event.key === "ArrowDown"
          ? index + 1
          : null;
    if (to === null || !onMove || to < 0 || to >= rows.length) return;
    event.preventDefault();
    onMove(index, to);
  };

  return (
    <ol aria-label={ariaLabel} className="flex flex-col gap-1.5">
      {rows.map((row, index) => (
        <li
          key={row.key}
          draggable={canMove}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = "move";
            // Firefox starts a drag only once some data is set.
            event.dataTransfer.setData("text/plain", row.key);
            setDragFrom(index);
          }}
          onDragOver={(event) => {
            if (dragFrom === null) return;
            event.preventDefault();
            setDragOver(index);
          }}
          onDrop={(event) => {
            if (dragFrom === null) return;
            event.preventDefault();
            onMove?.(dragFrom, index);
            endDrag();
          }}
          onDragEnd={endDrag}
          className={`flex items-center gap-2.5 rounded-(--radius-md) border bg-(--surface-2) px-3 py-2 ${
            dragOver === index && dragFrom !== index
              ? "border-(--brand-primary)"
              : "border-(--border-subtle)"
          } ${dragFrom === index ? "opacity-50" : ""}`}
        >
          {canMove ? (
            <button
              type="button"
              aria-label={`Move ${row.name} (arrow keys)`}
              onKeyDown={(event) => handleGripKeyDown(event, index)}
              className="flex cursor-grab text-(--text-faint) hover:text-(--text-primary)"
            >
              <GripVertical size={14} />
            </button>
          ) : (
            <span className="w-3.5" />
          )}
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) px-1.5 text-[11px] font-bold text-(--brand-primary-contrast)">
            {startNumber + index}
          </span>
          <span className="flex-1 text-[13px] font-medium text-(--text-primary)">
            {row.label}
          </span>
          {onRemove && row.removable !== false ? (
            <RemoveButton name={row.name} onClick={() => onRemove(row.key)} />
          ) : null}
        </li>
      ))}
    </ol>
  );
};

export const RemoveButton = ({
  name,
  onClick,
}: {
  name: string;
  onClick: () => void;
}): JSX.Element => (
  <button
    type="button"
    aria-label={`Remove ${name}`}
    onClick={onClick}
    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-(--radius-md) text-(--text-muted) hover:bg-(--surface-1) hover:text-(--status-danger)"
  >
    <X size={14} />
  </button>
);

// ── new row: Category → Field ───────────────────────────────────────────────

const selectClass =
  "min-w-0 flex-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) px-2.5 py-1.5 text-[13px] text-(--text-primary) focus:border-(--brand-primary) focus:outline-none disabled:opacity-50";

// A row being added. Red until both a category and a field are chosen; picking
// the field hands it to `onPick`, which turns the draft into a real row.
// `categories` must already exclude used fields (see narrowCategories), so
// duplicates and exhausted categories are never offered.
export const FieldPicker = ({
  categories,
  onPick,
  onCancel,
  name,
}: {
  categories: AnalyticsCategory[];
  onPick: (key: AnalyticsFieldKey) => void;
  onCancel: () => void;
  name: string;
}): JSX.Element => {
  const [chosenId, setCategoryId] = useState("");
  // What is offered can shrink while the draft is open (a counter metric
  // narrows the properties): a choice no longer offered falls back to the sole
  // remaining category, or to none.
  const category =
    categories.find((c) => c.id === chosenId) ??
    (categories.length === 1 ? categories[0] : undefined);
  const categoryId = category?.id ?? "";

  return (
    <div
      role="group"
      aria-label={`New ${name}`}
      aria-invalid="true"
      className="flex items-center gap-2 rounded-(--radius-md) border border-(--status-danger) bg-(--status-danger)/5 px-3 py-2"
    >
      <select
        aria-label={`${name} category`}
        autoFocus={categories.length !== 1}
        value={categoryId}
        onChange={(event) => setCategoryId(event.target.value)}
        className={selectClass}
      >
        <option value="" disabled>
          Category…
        </option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
      <select
        aria-label={`${name} property`}
        // With one category left there is nothing to choose before this.
        autoFocus={categories.length === 1}
        value=""
        disabled={!category}
        onChange={(event) => onPick(event.target.value as AnalyticsFieldKey)}
        className={selectClass}
      >
        <option value="" disabled>
          Property…
        </option>
        {category?.fields.map((key) => (
          <option key={key} value={key}>
            {category.fieldDescriptors[key]?.label ?? key}
          </option>
        ))}
      </select>
      <RemoveButton name={`new ${name}`} onClick={onCancel} />
    </div>
  );
};

// The empty line under a list; adds a new row. Not rendered when there is
// nothing left to add.
// `autoFocus` hands focus back to it once a row was just added or cancelled,
// so the keyboard does not fall back to the page.
export const AddRowButton = ({
  label,
  onClick,
  autoFocus = false,
}: {
  label: string;
  onClick: () => void;
  autoFocus?: boolean;
}): JSX.Element => (
  <button
    type="button"
    autoFocus={autoFocus}
    onClick={onClick}
    className="flex w-full items-center gap-2 rounded-(--radius-md) border border-dashed border-(--border-subtle) bg-transparent px-3 py-2 text-left text-[12.5px] font-medium text-(--text-muted) transition-colors hover:border-(--brand-primary) hover:text-(--brand-primary)"
  >
    <Plus size={14} />
    {label}
  </button>
);
